/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  App,
  Button as ButtonAnt,
  Checkbox,
  Col,
  Form,
  FormProps,
  Input,
  Popover,
  Row,
  TableColumnsType,
  Typography
} from 'antd';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';
import { Icon } from '../icons';
import { Button } from '../button';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { Table } from '../tables/table';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

import {
  fetchPermissionFields,
  fetchPermissionModules,
  watchPermissionFields,
  watchPermissionModules
} from '@/redux/slices/permissions.slice';
import {
  getRoleById,
  resetMessages,
  updateRole,
  watchRoleDetail,
  watchRoleMessage
} from '@/redux/slices/roles.slice';
import { setMessages } from '@/redux/slices/app.slice';
import { PERMISSION } from '@/constants/permissions.constant';
import { generatePermissions } from '@/helpers/permissions.helper';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { Message } from '@/enums/message.enum';
import { AccessPermissionContext } from '@/hooks/access-permission.hook';
import { useBeforeUnload } from 'react-router-dom';

type FieldType = {
  name: string;
  desc: string;
  permissions: Record<string, boolean>;
};

export const RoleEditForm = () => {
  const { push, roleId } = useQuery();
  const [form] = Form.useForm();
  const { modal } = App.useApp();
  const dispatch = useAppDispatch();
  const permissionFields = useAppSelector(watchPermissionFields);
  const permissionModules = useAppSelector(watchPermissionModules);
  const roleDetail = useAppSelector(watchRoleDetail);
  const messageInfo = useAppSelector(watchRoleMessage);

  const permissionContext = useContext(AccessPermissionContext);

  const permissionsData = useMemo(
    () =>
      permissionModules.map((module) => ({
        index: `${module.index}`,
        name: module.name,
        permissionRelation: module.permissionRelation,
        children: (module.childrens || []).map((child: PermissionChild) => ({
          index: `${module.index}-${child.index}`,
          name: child.name,
          permissionRelation: child.permissionRelation
        }))
      })),
    [permissionModules]
  );

  const permissionsUser = useMemo(
    () => generatePermissions(roleDetail?.modulePermission || [], PERMISSION),
    [roleDetail]
  );

  const handleGoBack = useCallback(() => {
    push(PageURLs.ofRoleManagement());
  }, [push]);

  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    const modulePermission = Object.entries(values.permissions)
      .filter(([_, value]) => value)
      .map(([key]) => {
        const parts = key.split('-').map(Number);
        // Nếu có 2 phần: moduleIndex-permissionIndex (parent module)
        // Nếu có 3 phần: moduleIndex-childIndex-permissionIndex (child module)
        if (parts.length === 2) {
          return {
            moduleIndex: parts[0],
            permissionIndex: parts[1]
          };
        } else if (parts.length === 3) {
          return {
            moduleIndex: parts[1],
            permissionIndex: parts[2]
          };
        }
        return undefined;
      })
      .filter((item): item is { moduleIndex: number; permissionIndex: number } => item !== undefined);

    if (modulePermission.length <= 0) {
      alertSelectPermission();
      return;
    }

    const payload = {
      name: values.name,
      desc: values.desc ?? '',
      modulePermission
    };

    const result = await dispatch(
      updateRole({ roleId: Number(roleId), roleData: payload })
    );

    if (updateRole.fulfilled.match(result)) {
      permissionContext?.roleSettings(true);
      handleGoBack();
    }
  };

  const alertSelectPermission = () => {
    modal.confirm({
      title: 'Thông báo',
      content: <em>Phải có ít nhất 1 chức năng được phân quyền</em>,
      okText: 'Xác nhận',
      width: 450,
      cancelText: 'Hủy'
    });
  };

  const columns: TableColumnsType<PermissionChild> = [
    {
      title: 'Danh sách chức năng (Tên vai trò)',
      dataIndex: 'name',
      key: 'name'
    },
    ...permissionFields.map((col) => ({
      title: col.name,
      dataIndex: col.index,
      key: col.index,
      render: (_: string, record: PermissionChild) => {
        const hasPermission = record.permissionRelation?.includes(col.index);
        if (!hasPermission) return null;
        const permissionKey =
          `${record.index}-${col.index}` as keyof typeof PERMISSION;

        const fieldKey = record.index.split('-').length === 1
          ? `${record.index}-${col.index}`
          : `${record.index}-${col.index}`;

        return (
          <Popover placement='topLeft' content={PERMISSION[permissionKey]}>
            <Form.Item<FieldType>
              name={['permissions', fieldKey]}
              valuePropName='checked'
              noStyle
            >
              <Checkbox />
            </Form.Item>
          </Popover>
        );
      }
    }))
  ];

  useEffect(() => {
    dispatch(fetchPermissionFields());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchPermissionModules());
  }, [dispatch]);

  useEffect(() => {
    if (!roleId) {
      handleGoBack();
    } else {
      dispatch(getRoleById(roleId));
    }
  }, [roleId, handleGoBack, dispatch]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      if (messageInfo.type === Message.SUCCESS) {
        handleGoBack();
      }
      dispatch(resetMessages());
    }
  }, [dispatch, handleGoBack, messageInfo, form]);

  useEffect(() => {
    if (roleDetail) {
      form.setFieldsValue({
        name: roleDetail.name,
        desc: roleDetail.desc,
        permissions: permissionsUser
      });
    }
  }, [roleDetail, form, permissionsUser]);

  useBeforeUnload((e) => {
    e.preventDefault();
  });

  return (
    <div css={rootStyles}>
      <Typography.Title css={titleStyles} level={5}>
        <ButtonAnt
          css={goBackBtnStyles}
          onClick={handleGoBack}
          type='text'
          icon={<Icon type='arrowLeft' width={20} height={20} />}
        />
        Chỉnh sửa vai trò
      </Typography.Title>
      <Form
        form={form}
        name='create-admin'
        css={formStyles}
        initialValues={{ remember: true }}
        onFinish={onFinish}
        autoComplete='off'
        className='ant-form-custom'
      >
        <Form.Item<FieldType>
          label='Tên vai trò (*)'
          name='name'
          rules={[{ required: true, message: 'Tên vai trò là bắt buộc' }]}
          initialValue={roleDetail?.name}
        >
          <Input size='large' placeholder='Nhập tên vai trò' />
        </Form.Item>

        <Form.Item<FieldType>
          label='Mô tả'
          name='desc'
          initialValue={roleDetail?.desc}
        >
          <Input.TextArea rows={1} size='large' placeholder='Nhập mô tả' />
        </Form.Item>
        {permissionsData.length > 0 && (
          <Form.Item<FieldType> label='Danh sách chức năng' name='desc'>
            <Table
              rowKey={(record) => record.index}
              columns={columns as any}
              dataSource={permissionsData}
              expandable={{
                defaultExpandAllRows: true,
                expandIcon: ({ expanded, onExpand, record }) =>
                  record.children && record.children.length > 0 ? (
                    <Button
                      type='text'
                      style={{
                        background: 'none',
                        border: 'none',
                        boxShadow: 'none'
                      }}
                      onClick={(e) => onExpand(record, e)}
                      icon={
                        expanded ? <CaretDownOutlined /> : <CaretUpOutlined />
                      }
                    />
                  ) : (
                    <span
                      style={{
                        marginLeft: '3rem',
                        display: 'inline-block'
                      }}
                    />
                  )
              }}
              pagination={false}
            />
          </Form.Item>
        )}
        <Row justify='center' gutter={[20, 0]}>
          <Col>
            <Button onClick={handleGoBack} css={actionBtnStyles} shape='round'>
              Huỷ bỏ
            </Button>
          </Col>
          <Col>
            <Button
              css={actionBtnStyles}
              htmlType='submit'
              type='primary'
              shape='round'
            >
              Lưu
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem 2.4rem;
`;

const formStyles = css`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  .ant-form-item {
    .ant-form-item-label {
      width: 12rem;
      text-align: left;
      font-weight: 500;
      line-height: 4rem;
    }
  }
`;

const titleStyles = css`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  position: relative;
  left: 2rem;
  margin-bottom: 0 !important;
`;

const goBackBtnStyles = css`
  position: absolute;
  left: -3rem;
  width: 2.6rem !important;
  height: 2.6rem !important;
  padding: 0;
  svg {
    margin-top: 0.1rem;
  }
  &:hover {
    border-radius: 50%;
  }
`;

const actionBtnStyles = css`
  width: 10rem;
  font-weight: 500;
`;
